<template>
  <div>
    <v-row>
      <v-col class="noYPadding">
        <VTextField
          v-model="component.name"
          hideDetails
          outlined
          disabled
          data-cy="component-Name"
          :label="$t('forms.labels.componentName')"
          @input="updateData"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col class="noYPadding">
        <VSelect
          v-model="data.dataSource"
          hideDetails
          outlined
          :disabled="isDisabled"
          data-cy="productLevel-channel"
          :label="$t('devices.flowMeters.sources.label')"
          :items="sources"
          itemValue="value"
          itemText="name"
          @input="updateData"
        />
      </v-col>
    </v-row>

    <!-- This VSelect will only be displayed if productLevel.dataSource is 'IOT' -->
    <v-row v-if="matchSource(SOURCE_IOT)">
      <v-col class="noYPadding">
        <!-- Assuming you have another VSelect to show -->
        <VSelect
          v-model="data.productLevelHub"
          hideDetails
          outlined
          :disabled="isDisabled"
          data-cy="productLevelHub-source"
          :label="$t('devices.productLevel.sensorName')"
          :rules="{ required: true }"
          :items="availableSensors"
          @input="updateData"
        />
      </v-col>
    </v-row>
    <!-- This VSelect will only be displayed if productLevel.dataSource is 'CONTROLLER'  -->
    <v-row v-if="matchSource(SOURCE_CONTROLLER)">
      <v-col class="noYPadding">
        <VSelect
          v-model="data.controller"
          hideDetails
          outlined
          :disabled="isDisabled"
          data-cy="flowMeter-controllerHub"
          :label="$t('devices.controllersHubs.name')"
          :rules="{ required: true }"
          :items="availableControllersHubs"
          @input="updateData"
        />
      </v-col>
    </v-row>
    <!-- This VSelect will only be displayed if productLevel.dataSource is 'CONTROLLER' AND the controller hub is selected  -->
    <v-row v-if="matchSource(SOURCE_CONTROLLER) && data.controller">
      <v-col class="noYPadding">
        <VSelect
          v-model="data.probe"
          hideDetails
          outlined
          :disabled="isDisabled"
          data-cy="productLevel-probeName"
          :label="$t('devices.probes.name')"
          :rules="{ required: true }"
          :items="availableProbes"
          itemText="name"
          itemValue="id"
          @input="updateData"
          @change="probeSelected(data.probe)"
        />
      </v-col>
    </v-row>
    <v-row v-if="matchSource(SOURCE_IOT) && data.productLevelHub">
      <v-col class="noYPadding">
        <VTextField
          v-model="data.macAddress"
          :label="$t('devices.macAddress')"
          :rules="{ required: true, min: 1, max: 17, regex:/^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/ }"
          hideDetails
          outlined
          disabled
          data-cy="productLevel-macAddress"
          @input="updateData"
        />
      </v-col>
    </v-row>
    <v-row v-if="matchSource(SOURCE_CONTROLLER) && data.controller">
      <v-col class="noYPadding">
        <VTextField
          v-model="probeId"
          :label="$t('devices.productLevel.probeId')"
          hideDetails
          disabled
          outlined
          data-cy="productLevel-probeId"
        />
      </v-col>
    </v-row>
    <v-row v-if="matchSource(SOURCE_MANUAL)">
      <v-col class="noYPadding">
        <VTextField
          v-model="data.productLevelName"
          hideDetails
          :rules="{ required: true }"
          outlined
          data-cy="productLevel-productLevelName"
          :label="$t('devices.productLevel.productLevelName')"
          @input="updateData"
        />
      </v-col>
    </v-row>
    <v-row
      v-if="matchSource(SOURCE_MANUAL)
        || (matchSource(SOURCE_CONTROLLER) && data.controller)
        || (matchSource(SOURCE_IOT) && data.productLevelHub)"
    >
      <v-col class="noYPadding">
        <VSelect
          v-model="data.productName"
          hideDetails
          outlined
          :disabled="isDisabled"
          data-cy="productLevel-name"
          :label="$t('devices.productLevel.productName')"
          :rules="{ required: true }"
          :items="products"
          @input="updateData"
        />
      </v-col>
    </v-row>

    <v-row
      v-if="matchSource(SOURCE_MANUAL)
        || (matchSource(SOURCE_CONTROLLER) && data.controller)
        || (matchSource(SOURCE_IOT) && data.productLevelHub)"
    >
      <v-col class="noYPadding">
        <VSelect
          v-model="data.productContainer"
          hideDetails
          outlined
          data-cy="productLevel-productContainer"
          :label="$t('administration.productContainer')"
          :rules="{ required: true }"
          :items="containers"
          @input="updateData"
        />
      </v-col>
    </v-row>

    <div
      v-if="data.productContainer === 'custom'"
    >
      <v-row>
        <v-col>
          <VSelect
            v-model="data.productContainerCustom.shape"
            :label="$t('productContainers.shape')"
            :rules="{ required: teamMainLang === selectedLang, min: 1, max: 200 }"
            data-cy="name-text"
            outlined
            :items="shapesFormList"
            @input="updateData"
          />
        </v-col>
      </v-row>
      <v-row
        align="center"
        justify="center"
      >
        <v-col>
          <VSelect
            v-model="data.productContainerCustom.unit"
            :label="$t('productContainers.unit')"
            :rules="{ required: teamMainLang === selectedLang, min: 1, max: 200 }"
            data-cy="name-text"
            outlined
            :items="getUnitByShape()"
            @input="updateData"
          />
        </v-col>
      </v-row>

      <div
        v-if="data.productContainerCustom.shape === 'cylinder'"
      >
        <v-row
          align="left"
          justify="left"
        >
          <v-col>
            <v-radio-group
              v-model="data.productContainerCustom.cylinder.type"
              row
            >
              <v-radio
                v-for="itm in baseSizesList"
                :key="itm"
                :label="itm.name"
                :value="itm.id"
              >
                <template v-slot:label>
                  <div style="font-weight: 500;">
                    {{ itm.name }}
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </v-col>
        </v-row>

        <v-row
          v-if="data.productContainerCustom.shape === 'cylinder' && data.productContainerCustom.cylinder.type"
          align="center"
          justify="center"
        >
          <v-col>
            <VTextField
              v-model.number="data.productContainerCustom.cylinder.radiusOrDiameterValue"
              :label="data.productContainerCustom.cylinder.type === 'radius'
                ? $t('productContainers.radiusValue')
                : $t('productContainers.diameterValue')"
              :rules="{ required: true }"
              data-cy="name-text"
              outlined
              :suffix="data.productContainerCustom.unit"
              @input="updateData"
            />
          </v-col>
          <v-col>
            <VTextField
              v-model.number="data.productContainerCustom.cylinder.heightValue"
              :label="$t('productContainers.heightValue')"
              :rules="{ required: true }"
              data-cy="name-text"
              outlined
              :suffix="data.productContainerCustom.unit"
              @input="updateData"
            />
          </v-col>
        </v-row>
      </div>

      <div
        v-if="data.productContainerCustom.shape === 'rectangular' && data.productContainerCustom.unit"
      >
        <v-row
          align="center"
          justify="center"
        >
          <v-col>
            <VTextField
              v-model.number="data.productContainerCustom.rectangular.lengthValue"
              :label="$t('productContainers.lengthValue')"
              :rules="{ required: true }"
              outlined
              :suffix="data.productContainerCustom.unit"
              @input="updateData"
            />
          </v-col>
          <v-col>
            <VTextField
              v-model.number="data.productContainerCustom.rectangular.widthValue"
              :label="$t('productContainers.widthValue')"
              :rules="{ required: true }"
              outlined
              :suffix="data.productContainerCustom.unit"
              @input="updateData"
            />
          </v-col>
          <v-col>
            <VTextField
              v-model.number="data.productContainerCustom.rectangular.heightValue"
              :label="$t('productContainers.heightValue')"
              :rules="{ required: true }"
              outlined
              :suffix="data.productContainerCustom.unit"
              @input="updateData"
            />
          </v-col>
        </v-row>
      </div>
    </div>
    <template
      v-if="matchSource(SOURCE_MANUAL)
        || (matchSource(SOURCE_CONTROLLER) && data.controller)
        || (matchSource(SOURCE_IOT) && data.productLevelHub)"
    >
      <v-row class="readingRow">
        <v-col class="noYPadding">
          <div class="readingHead">
            {{ $t('devices.productLevel.reading') }}
          </div>
          <div class="readingCard">
            <div class="readingCardInput">
              <VTextField
                v-model="data.reading"
                hideDetails
                type="number"
                :rules="{ required: true, min: 1}"
                data-cy="productLevel-reading"
                @input="updateData"
              />
            </div>
            <VSelect
              v-model="data.readingMeasureUnits"
              hideDetails
              outlined
              :disabled="disabledIotReading()"
              :rules="{ required: true }"
              class="readingSelect"
              data-cy="productLevel-readingMeasureUnits"
              :items="genericReadingUnit"
              @input="updateData"
            />
          </div>
        </v-col>

        <span class="stylEgual">=</span>
        <v-col class="noYPadding">
          <div class="readingHead">
            {{ $t('devices.productLevel.report') }}
          </div>
          <div class="readingCard">
            <div class="readingCardInput">
              <VTextField
                v-model="data.report"
                hideDetails
                type="number"
                :rules="{ required: true, min: 1 }"
                data-cy="productLevel-report"
                @input="updateData"
              />
            </div>
            <VSelect
              v-model="data.reportMeasureUnits"
              hideDetails
              outlined
              class="readingSelect"
              :rules="{ required: true }"
              data-cy="productLevel-reportMeasureUnits"
              :items="genericVolumes"
              @input="updateData"
            />
          </div>
        </v-col>
      </v-row>

      <v-row class="readingRow">
        <v-col class="noYPadding">
          <VTextField
            v-model="data.maxLevel"
            hideDetails
            outlined
            type="number"
            :rules="{ required: true, min: 1 }"
            data-cy="productLevel-maxLevel"
            :label="$t('devices.productLevel.maxLevel')"
            @input="updateData"
          />
        </v-col>
        <span />
        <v-col class="noYPadding">
          <VSelect
            v-model="data.maxLevelMeasureUnits"
            hideDetails
            outlined
            disabled
            :rules="{ required: true }"
            data-cy="productLevel-maxLevelMeasureUnits"
            :label="$t('devices.measureUnit')"
            :items="genericReadingUnit"
            @input="updateData"
          />
        </v-col>
      </v-row>
      <v-row class="readingRow">
        <v-col class="noYPadding">
          <VTextField
            v-model="data.lowLevel"
            hideDetails
            outlined
            type="number"
            :rules="{ required: true , min: 1}"
            data-cy="productLevel-lowLevel"
            :label="$t('devices.productLevel.lowLevel')"
            @input="updateData"
          />
        </v-col>
        <span />
        <v-col class="noYPadding">
          <VSelect
            v-model="data.lowLevelMeasureUnits"
            hideDetails
            outlined
            disabled
            :rules="{ required: true }"
            data-cy="productLevel-lowLevelMeasureUnits"
            :label="$t('devices.measureUnit')"
            :items="genericReadingUnit"
            @input="updateData"
          />
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import { SOURCE_IOT, SOURCE_CONTROLLER, SOURCE_MANUAL, TYPE_MAKE_UP, TYPE_BLOWDOWN, TYPE_FEED, TYPE_STEAM, TYPE_CONDENSATE } from '@/aquassist-shared/constants/types/flowMeters';
import { mapGetters } from 'vuex';
import { genericVolumes } from '@/aquassist-shared/constants/volumes';
import { isEmpty } from 'lodash';
import { SHAPES_TYPES, CYLINDER_UNIT, RECTANGULAR_UNIT, BASE_SIZES } from '@/aquassist-shared/constants/productContainers';

export default {
    name: 'LinkProductLevelForm',
    components: {
        VSelect,
        VTextField,
    },
    props: {
        productMeterSelected: {
            type: Object,
            default: () => ({}),
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        component: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            data: {
                component: '',
                macAddress: '',
                productName: '',
                productLevelName: '',
                dataSource: '',
                productLevelHub: null,
                controller: null,
                readingMeasureUnits: '',
                reportMeasureUnits: '',
                maxLevelMeasureUnits: '',
                lowLevelMeasureUnits: '',
                reading: 1,
                report: 0,
                maxLevel: 0,
                lowLevel: 0,
                probe: null,
                productContainer: null,
                productContainerCustom: {
                    shape: null,
                    unit: null,
                    cylinder: {
                        type: '', // Could be only diameter or Radius
                        radiusOrDiameterValue: 0,
                        heightValue: 0,
                    },
                    rectangular: {
                        lengthValue: 0,
                        widthValue: 0,
                        heightValue: 0,
                    },
                },
            },
            probeId: null,
            availableMeters: [],
            availableMetersHubs: [], // to be remove
            availableSensors: [],
            availableControllersHubs: [],
            availableProbes: [],
            products: [],
            isDisabled: false,
            containers: [],
            dataJustLoaded: false,
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'site',
            'userLang',
        ]),
        genericVolumes() {
            return genericVolumes.filter(v => v.tag.includes('productLevel')).map(p => ({
                name: this.$t(`devices.productUnitMeasure.${p.name.toLowerCase()}`),
                id: p.name,
            }));
        },
        genericReadingUnit() {
            return genericVolumes
                .filter(v => ['productLevel', 'productLevelReading'].some(tag => v.tag.includes(tag)))
                .map(p => ({
                    name: this.$t(`devices.productUnitMeasure.${p.name.toLowerCase()}`),
                    id: p.name,
                }));
        },
        sources() {
            return [
                {
                    name: this.$t(`devices.flowMeters.sources.${SOURCE_IOT}`),
                    value: SOURCE_IOT,
                },
                {
                    name: this.$t(`devices.flowMeters.sources.${SOURCE_CONTROLLER}`),
                    value: SOURCE_CONTROLLER,
                },
                {
                    name: this.$t(`devices.flowMeters.sources.${SOURCE_MANUAL}`),
                    value: SOURCE_MANUAL,
                },
            ];
        },
        SOURCE_IOT() {
            return SOURCE_IOT;
        },
        SOURCE_CONTROLLER() {
            return SOURCE_CONTROLLER;
        },
        SOURCE_MANUAL() {
            return SOURCE_MANUAL;
        },
        types() {
            return [TYPE_MAKE_UP, TYPE_BLOWDOWN, TYPE_FEED, TYPE_STEAM, TYPE_CONDENSATE].map(type => ({
                name: this.$t(`devices.flowMeters.types.${type}`),
                value: type,
            }));
        },
        shapesFormList() {
            const shapesList = SHAPES_TYPES.map(value => ({ id: value, name: this.$t(`productContainers.form.shapes.${value}`) }));
            return shapesList;
        },
        baseSizesList() {
            const baseSizesList = BASE_SIZES.map(value => ({ id: value, name: this.$t(`productContainers.${value}`) }));
            return baseSizesList;
        },
    },
    watch: {
        // Watch for changes in the selected hub
        'data.controller': {
            immediate: false,
            async handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    await this.getAvailableControllersHubs();
                    await this.getAvailableProbes();
                }
            },
        },
        'data.productLevelHub': {
            immediate: false,
            async handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    await this.getAvailableSensors();
                    await this.getProductLevelIotMacAdress();
                }
            },
        },
        'data.reportMeasureUnits': {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.data.lowLevelMeasureUnits = newVal;
                    this.data.maxLevelMeasureUnits = newVal;
                }
            },
        },
    },
    async mounted() {
        this.getProductLevelDataSelected();
        await this.getAvailableSensors();
        await this.getAvailableControllersHubs();
        await this.getSiteProducts();
        await this.getProductContainers();
        this.emitChange();
        this.$nextTick(() => {
            if (this.editMode) this.dataJustLoaded = false;
        });
    },
    methods: {
        getProductLevelDataSelected() {
            if (!this.editMode) {
                this.data.component = this.component.id;
                this.data.productContainer = null;
                this.data.productContainerCustom = {
                    shape: null,
                    unit: null,
                    cylinder: {
                        type: '',
                        radiusOrDiameterValue: 0,
                        heightValue: 0,
                    },
                    rectangular: {
                        lengthValue: 0,
                        widthValue: 0,
                        heightValue: 0,
                    },
                };
            } else {
                this.dataJustLoaded = true;
                this.isDisabled = true;
                const {
                    component, name, macAddress, id,
                    dataSource, maxLevel, minLevel,
                    readingValue, reportValue, siteProduct,
                    readingUnits, reportUnits, maxLevelUnits, minLevelUnits,
                    productContainerCustom, productContainer,
                } = this.productMeterSelected.productMeter;
                this.data = {
                    component,
                    dataSource,
                    productLevelName: name,
                    maxLevel,
                    lowLevel: minLevel,
                    reading: readingValue,
                    report: reportValue,
                    productName: siteProduct,
                    readingMeasureUnits: readingUnits,
                    reportMeasureUnits: reportUnits,
                    maxLevelMeasureUnits: maxLevelUnits,
                    lowLevelMeasureUnits: minLevelUnits,
                    macAddress,
                    productLevelHub: id,
                    productContainer: productContainer || null,
                    productContainerCustom: productContainerCustom || null,
                    // eslint-disable-next-line no-underscore-dangle
                    controller: this.productMeterSelected?.controller?._id ?? null,
                    probe: this.productMeterSelected?.probe?.id ?? null,
                };
                this.probeId = this.productMeterSelected?.probe?.probeId ?? null;
            }
        },
        async getSiteProducts() {
            const { data } = await this.$rq.getSiteProducts(this.site.id, { pagination: false, notAssigned: true, component: this.component.id });
            this.products = data.products.map(product => ({
                name: product.name,
                id: product.id,
            }))
                .sort((a, b) => a.name.localeCompare(b.name));
        },
        probeSelected(probe) {
            if (probe) {
                const selectedProbe = this.availableProbes.find(p => p.id === probe);
                this.probeId = selectedProbe.probeId;
            }
        },
        updateData() {
            this.emitChange();
            this.$emit('sendData', this.data);
        },
        async getAvailableProbes() {
            if (!this.data.controller) return;
            const selectedHub = this.availableControllersHubs.find(hub => hub.id === this.data.controller);
            if (selectedHub) {
                this.availableProbes = this.editMode
                    ? selectedHub.probes.filter(detail => detail.id === this.data.probe)
                    : (selectedHub.probes ?? []).filter(detail => detail.component === undefined);
            } else {
                this.availableProbes = [];
            }
        },
        async getProductLevelIotMacAdress() {
            if (!this.data.productLevelHub) return;
            const selectedHubIot = this.availableSensors.find(hub => hub.id === this.data.productLevelHub);
            if (selectedHubIot) {
                this.data.macAddress = selectedHubIot.macAddress;
            } else {
                this.data.macAddress = '';
            }
        },
        async getAvailableSensors() {
            const { data } = await this.$rq.getAllProductSensorsHub(this.site.id);
            this.availableSensors = this.editMode
                ? data.filter(sensor => sensor.id === this.data.productLevelHub)
                : data.filter(sensor => !sensor.component.id && !sensor.network.id);
        },
        async getAvailableControllersHubs() {
            const availableControllersHubs = await this.$rq.getControllers(this.site.id);
            this.availableControllersHubs = availableControllersHubs.data.controllers;
        },
        matchSource(value) {
            return this.data.dataSource === value;
        },
        linkProductLevelValidate() {
            const hasValidFields = field => this.data[field] && this.data[field].length > 0;
            const isValid = [
                'reportMeasureUnits',
                'readingMeasureUnits',
                'maxLevelMeasureUnits',
                'lowLevelMeasureUnits',
            ].every(hasValidFields);
            const hasProductName = !isEmpty(this.data.productName);
            const hasReading = this.data.reading !== null && this.data.reading !== undefined && Number(this.data.reading) > 0;
            const hasReport = this.data.report !== null && this.data.report !== undefined && Number(this.data.report) > 0;
            const hasDataSource = this.data.dataSource && this.data.dataSource.length > 0;
            if (!hasDataSource || !isValid || !hasProductName || !hasReading || !hasReport) return false;
            switch (this.data.dataSource) {
                case SOURCE_MANUAL:
                    return hasValidFields('productLevelName');
                case SOURCE_IOT:
                    return hasValidFields('macAddress') && this.data.productLevelHub;
                case SOURCE_CONTROLLER:
                    return this.data.controller && this.data.probe && this.probeId;
                default:
                    return false;
            }
        },
        emitChange() {
            this.$emit('inputChange', this.linkProductLevelValidate);
        },
        disabledIotReading() {
            if (this.data.dataSource === SOURCE_IOT) {
                this.data.readingMeasureUnits = 'mm';
                return true;
            }
            return false;
        },
        async getProductContainers() {
            const { data } = await this.$rq.getProductContainers();

            this.containers = data.productContainers.map(container => ({
                id: container.id,
                name: container.name[this.userLang] || container.name.en,
            }));

            const itemToAdd = {
                id: 'custom',
                name: this.$t('forms.labels.custom'),
            };
            this.containers.unshift(itemToAdd);
        },
        getUnitByShape() {
            switch (this.data.productContainerCustom.shape) {
                case 'cylinder':
                    return CYLINDER_UNIT.map(value => ({ id: value, name: this.$t(`productContainers.units.${value}`) }));
                case 'rectangular':
                    return RECTANGULAR_UNIT.map(value => ({ id: value, name: this.$t(`productContainers.units.${value}`) }));
                default:
                    return [];
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.stylEgual{
    font-size: 40px;
    font-weight: bold;
    width: 40px;
    text-align: center;
}
.readingCard{
    border: 1px solid #9e9e9e;
    border-radius: 5px;
    &Input {
        padding: 0 2rem 20px;

        ::v-deep .v-input input {
            text-align: center;
        }
    }

}
.readingSelect{
    ::v-deep .v-text-field--outlined fieldset {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        border: none;
        border-top: 1px solid #9e9e9e;
        background-color: rgba(247,248,249,255);
    }
}
.readingHead{
    top: 30px;
    transform: translateX(10px) translateY(10px);
    background-color: white;
    width: 70px;
    text-align: center;
}
.readingRow{
    display: grid;
    grid-template-columns: 1fr 40px 1fr;
    align-items: center;
}
</style>
